import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Tag, 
  Typography, 
  Table, 
  Input, 
  Space, 
  Dropdown, 
  Button, 
  App, 
  Empty 
} from 'antd';
import { 
  MoreOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  StarFilled,
  EditFilled,
  CheckCircleFilled,
  BoxPlotOutlined,
  CopyOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { listRoutines, deleteRoutine, cloneRoutine } from '../../services/routines';
import { Tooltip } from 'antd';

const { Title, Text } = Typography;
const { useApp } = App;

const RoutinesList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  // Carga de datos optimizada
  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const params = {
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
      };

      const { items, total } = await listRoutines(params);
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total: total || 0 
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de planes.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Manejo de Acciones (RN-10: Validación de eliminación para publicados)
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'clone':
        modal.confirm({
          title: '¿Clonar rutina?',
          content: `Se creará una copia de "${record.title || 'Rutina'}".`,
          okText: 'Clonar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await cloneRoutine(record.id);
              message.success('Rutina clonada correctamente');
              fetchData(
                pagination.current,
                pagination.pageSize,
                sorter,
                search,
                // levelFilter,
                // typeFilter,
                // durationFilter
              );
            } catch {
              message.error('No se pudo clonar la rutina');
            }
          },
        });
        break;
      case 'view':
        navigate(`/routines/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/routines/${record.id}/edit`);
        break;
      case 'delete':
        // RN-10 / CA-15: No eliminar si está Publicado
        const statusName = (record.status?.name || record.status?.code || '').toLowerCase();
        if (statusName === 'publicado') {
          modal.warning({
            title: 'No se puede eliminar el plan',
            content: `El plan "${record.title}" se encuentra en estado PUBLICADO. Debes cambiar su estado a Borrador antes de poder eliminarlo.`,
            okText: 'Entendido',
          });
          return;
        }

        modal.confirm({
          title: `¿Deseas eliminar el plan ${record.title}?`,
          content: 'Esta acción realizará una eliminación lógica del plan.',
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteRoutine(record.id);
              message.success('Plan eliminado correctamente');
              
              const nextPage = (data.length === 1 && pagination.current > 1) 
                ? pagination.current - 1 
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el plan');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const isPublished = record.status?.code === 'published';
    const menuItems = [
      {
        key: 'clone',
        icon: <CopyOutlined />,
        label: 'Clonar',
      },
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver',
      },
      ...(!isPublished
      ? [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Editar',
          },
        ]
      : []),
      {
        type: 'divider',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Eliminar',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  // Definición de Columnas de la Tabla según requerimiento de diseño
  const columns = [
    {
      title: 'PLAN',
      key: 'plan',
      render: (_, r) => {

        return (
          <div>
              <Text bold style={{ fontSize: '15px', display: 'block', color: '#1f1f1f' }}>
                {r.title || r.nombre || '-'}
              </Text>
              <Text type="secondary" style={{ fontSize: '13px', display: 'block', maxWidth: 360 }}>
                {r.short_description || r.descripcion || 'Sin descripción corta.'}
              </Text>
            </div>
        );
      },
      sorter: (a, b) => (a.title || a.nombre || '').localeCompare(b.title || b.nombre || ''),
    },
    {
      title: 'NIVEL',
      dataIndex: ['level', 'name'],
      key: 'level',
      render: (_, r) => (
        <Text style={{ fontSize: '14px', color: '#262626' }}>
          {r.level?.name || r.nivel?.nombre || r.level_name || '-'}
        </Text>
      ),
    },
    {
      title: 'ENFOQUE',
      dataIndex: ['focus', 'name'],
      key: 'focus',
      render: (_, r) => {
        const focusName = r.focus?.name || r.enfoque?.nombre || r.focus_name;
        if (!focusName) return <Text type="secondary">-</Text>;
        return (
          <Tag 
            style={{ 
              backgroundColor: '#f0f0ff', 
              color: '#4f46e5', 
              border: 'none', 
              borderRadius: 6, 
              padding: '2px 10px',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            {focusName}
          </Tag>
        );
      },
    },
    {
      title: 'CONTEXTO',
      dataIndex: 'contexts',
      key: 'contexts',
      render: (_, r) => {
        // 1. Si el backend ya envía un string formateado
        if (typeof r.contexts_formatted === 'string' && r.contexts_formatted.trim()) {
          return <Text style={{ fontSize: '14px', color: '#595959' }}>{r.contexts_formatted}</Text>;
        }

        // 2. Si viene la relación `contexts` (array de RoutineContext)
        const contextsList = r.contexts || r.contextos;
        if (Array.isArray(contextsList) && contextsList.length > 0) {
          const formattedItems = contextsList.map((ctx) => {
            // Si el elemento es simplemente una cadena de texto
            if (typeof ctx === 'string') return ctx;

            // Nombre de la locación (Gimnasio, Casa, Parque, etc.)
            const locName = ctx.location?.name || ctx.location_name || ctx.nombre || ctx.name;
            // Nombre del equipamiento asociado si aplica
            const eqName = ctx.equipment?.name || ctx.equipment_name;

            if (locName && eqName) {
              return `${locName} (${eqName})`;
            }
            return locName || eqName || null;
          }).filter(Boolean);

          if (formattedItems.length > 0) {
            return (
              <Text style={{ fontSize: '14px', color: '#595959' }}>
                {formattedItems.join(', ')}
              </Text>
            );
          }
        }

        // 3. Fallback a campos individuales directos
        const singleContext = r.contexto?.nombre || r.context_name || r.context;
        return (
          <Text style={{ fontSize: '14px', color: '#595959' }}>
            {singleContext || '-'}
          </Text>
        );
      },
    },
    {
      title: 'DURACIÓN',
      key: 'duration',
      render: (_, r) => {
        // 1. Evaluar Semanas (Duración del plan)
        const weeks = r.duration_weeks ?? r.duracionSemanas ?? r.duration?.weeks;
        if (weeks !== undefined && weeks !== null && weeks !== '') {
          return <Text style={{ fontSize: '14px', color: '#262626' }}>{weeks} sem</Text>;
        }

        // 2. Evaluar Arreglo de Frecuencias (si viene presente y con elementos)
        if (Array.isArray(r.frequencies) && r.frequencies.length > 0) {
          const formattedFrequencies = r.frequencies
            .map(f => f.name || (f.days ? `${f.days} días` : null))
            .filter(Boolean)
            .join(', ');

          if (formattedFrequencies) {
            return (
              <Text style={{ fontSize: '14px', color: '#262626' }}>
                {formattedFrequencies}
              </Text>
            );
          }
        }

        // 3. Evaluar Días o Meses explícitos
        const days = r.duration_days ?? r.duracionDias ?? r.duration?.days;
        if (days !== undefined && days !== null && days !== '') {
          return <Text style={{ fontSize: '14px', color: '#262626' }}>{days} días</Text>;
        }

        const months = r.duration_months ?? r.duracionMeses ?? r.duration?.months;
        if (months !== undefined && months !== null && months !== '') {
          return <Text style={{ fontSize: '14px', color: '#262626' }}>{months} meses</Text>;
        }

        // 4. Fallback genérico
        const genericValue = r.duration_value || r.duration?.name;
        return (
          <Text style={{ fontSize: '14px', color: '#262626' }}>
            {genericValue || '-'}
          </Text>
        );
      },
    },
    {
      title: 'PRECIO',
      dataIndex: ['plan', 'name'],
      key: 'plan',
      render: (_, r) => {
        const rawPriceType = r.plan?.name || r.plan?.code || 'GRATIS';
        const priceType = String(rawPriceType).toUpperCase();
        const isFree = priceType === 'GRATIS' || priceType === 'FREE' || priceType === 'GRATUITA';

        if (isFree) {
          return (
            <Tag 
              style={{ 
                backgroundColor: '#f6ffed', 
                color: '#389e0d', 
                borderColor: '#b7eb8f',
                borderRadius: 6, 
                padding: '2px 12px',
                fontWeight: 600,
                fontSize: '13px'
              }}
            >
              Gratis
            </Tag>
          );
        }

        return (
          <Tag 
            icon={<StarFilled style={{ color: '#d48806', marginRight: 4 }} />}
            style={{ 
              backgroundColor: '#fffbe6', 
              color: '#d48806', 
              borderColor: '#ffe58f',
              borderRadius: 6, 
              padding: '2px 12px',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Suscripción
          </Tag>
        );
      },
    },
    {
      title: 'ESTADO',
      dataIndex: ['status', 'name'],
      key: 'status',
      render: (_, r) => {
        const statusName = (r.status?.name || r.estado || 'BORRADOR').toUpperCase();

        if (statusName === 'PUBLICADO') {
          return (
            <Tag 
              icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
              style={{ borderRadius: 6, padding: '2px 10px', backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f' }}
            >
              {statusName}
            </Tag>
          );
        }

        if (statusName === 'ARCHIVADO') {
          return (
            <Tag 
              icon={<BoxPlotOutlined />}
              style={{ borderRadius: 6, padding: '2px 10px', backgroundColor: '#fafafa', color: '#8c8c8c' }}
            >
              {statusName}
            </Tag>
          );
        }

        // Default: BORRADOR
        return (
          <Tag 
            icon={<EditFilled style={{ color: '#8c8c8c' }} />}
            style={{ 
              backgroundColor: '#f5f5f5', 
              color: '#595959', 
              border: 'none',
              borderRadius: 6, 
              padding: '4px 12px',
              fontWeight: 600
            }}
          >
            {statusName}
          </Tag>
        );
      },
    },
    {
      title: 'ACCIONES',
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '18px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 32px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
    
      {/* Tarjeta Principal */}
      <Card 
        title={<Title level={3} style={{ marginBottom: 0 }}>Catálogo de Planes</Title>}
        extra={
          <Space>
            <Tooltip title="Agregar ejercicio">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/routines/add')}
              >
                Nuevo plan
              </Button>
            </Tooltip>
          </Space>
        }
        bordered={false} 
        style={{ borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
        bodyStyle={{ padding: 24 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={20}>
          {/* Buscador de Planes */}
          <Input.Search
            placeholder="Buscar plan por nombre..."
            allowClear
            size="large"
            onSearch={onSearch}
            onChange={(e) => {
              if (!e.target.value) onSearch('');
            }}
            style={{ width: '100%', maxWidth: '100%' }}
          />

          {/* Tabla de Planes */}
          <Table
            rowKey={(r) => r.id}
            columns={columns}
            dataSource={data}
            loading={loading}
            locale={{
              emptyText: search ? (
                <Empty description="No encontramos planes con ese nombre." />
              ) : (
                <Empty description="Aún no existen planes creados.">
                  <Button type="primary" onClick={() => navigate('/routines/add')}>
                    Crear primer plan
                  </Button>
                </Empty>
              )
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default RoutinesList;